import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAiStatus } from '@/lib/serverMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Activity, AlertTriangle, FileText, ListChecks, Pencil, Layers, Users, ArrowRight } from 'lucide-react';

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
        <div className="text-2xl font-semibold mt-1">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const ai = useAiStatus();
  useEffect(() => { api.get('/admin/dashboard').then(r => setD(r.data)); }, []);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const monthly = Object.entries(d.monthly_volume || {}).sort().slice(-6).map(([k, v]) => ({ month: k, count: v }));
  const setup = d.setup || {};
  const needsCatalog = setup.catalog_count === 0;
  const needsDoctor = setup.doctor_count === 0;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <h1 className="text-base lg:text-lg font-semibold tracking-tight mb-4">Practice Dashboard</h1>
      {/* §2 admin note (2026-09-06): both signup sub-steps are skippable and
          nothing nudged afterwards. Without a catalog the AI has nothing to match
          against; without a doctor nobody can sign. */}
      {(needsCatalog || needsDoctor) && (
        <Card className="mb-4 border-primary/30 bg-primary/5" data-testid="admin-setup-nudge">
          <CardContent className="pt-4 space-y-3 text-sm">
            <div className="font-medium">Finish setting up your practice</div>
            {needsCatalog && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-muted-foreground"><Layers className="h-4 w-4 shrink-0" /> Your procedure catalog is empty, so the AI has nothing to match a patient's reason to.</span>
                <Button asChild size="sm" variant="outline"><Link to="/admin/catalog" data-testid="admin-setup-catalog-link">Add procedures <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link></Button>
              </div>
            )}
            {needsDoctor && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4 shrink-0" /> No doctor has joined yet, so nothing can be signed.</span>
                <Button asChild size="sm" variant="outline"><Link to="/admin/users" data-testid="admin-setup-users-link">Invite a doctor <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link></Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {ai.loaded && !ai.configured && (
        <Card className="mb-4 border-[hsl(32_92%_40%)]/40 bg-[hsl(32_92%_40%)]/5" data-testid="admin-ai-not-connected">
          <CardContent className="pt-4 flex items-start gap-2 text-sm text-[hsl(32_92%_28%)]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">AI is not connected on this deployment yet</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Cases are prepared by keyword matching against your catalog with standard template text, and staff see a
                warning on each one. Contact support to connect it.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {d.email_failures > 0 && (
        <Card className="mb-4 border-[hsl(0_72%_40%)]/40 bg-[hsl(0_72%_40%)]/5" data-testid="admin-email-failures">
          <CardContent className="pt-4 flex items-start gap-2 text-sm text-[hsl(0_72%_40%)]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">
                {d.email_failures} document{d.email_failures === 1 ? '' : 's'} had an email that could not be delivered
                {' '}(last {d.email_failure_window_days ?? 30} days)
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Those patients or employers never got their link. Open each document to see the delivery log, share the
                link manually, and contact support if this keeps happening — the sending address may need re-verifying.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {d.stalled_auto_prep > 0 && (
        <Card className="mb-4 border-[hsl(32_92%_40%)]/40 bg-[hsl(32_92%_40%)]/5" data-testid="admin-stalled-auto-prep">
          <CardContent className="pt-4 flex items-start gap-2 text-sm text-[hsl(32_92%_28%)]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">
                {d.stalled_auto_prep} paid case{d.stalled_auto_prep === 1 ? ' was' : 's were'} not AI pre-filled
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Nothing is lost — they are in the review queue, labelled manual. We already retried automatically and it
                still did not complete, so open them in Fast Review to fill them in (or press Run AI prep to try again).
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {d.stalled_in_review > 0 && (
        <Card className="mb-4 border-[hsl(32_92%_40%)]/40 bg-[hsl(32_92%_40%)]/5" data-testid="admin-stalled-in-review">
          <CardContent className="pt-4 flex items-start gap-2 text-sm text-[hsl(32_92%_28%)]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">
                {d.stalled_in_review} paid case{d.stalled_in_review === 1 ? ' has' : 's have'} been waiting with the
                care team for more than {Math.round((d.stalled_in_review_hours ?? 72) / 24)} days
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                The patient has paid and is waiting on a signature. Open the review queue, finish the clinical check and
                send them to the doctor.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* LB-CURE. The only card on this page with a statutory clock behind it
          (29 CFR 825.305(c)). Overdue is called out separately on purpose: "3
          open" is a workload, "1 past its deadline" is a patient who may have
          lost their leave protection. Nothing here is a task for admin staff —
          the doctor answers it — so the card says where it already is. */}
      {d.employer_cures > 0 && (
        <Card
          className={d.employer_cures_overdue > 0
            ? 'mb-4 border-destructive/40 bg-destructive/5'
            : 'mb-4 border-[hsl(32_92%_40%)]/40 bg-[hsl(32_92%_40%)]/5'}
          data-testid="admin-employer-cures"
        >
          <CardContent className={`pt-4 flex items-start gap-2 text-sm ${d.employer_cures_overdue > 0 ? 'text-destructive' : 'text-[hsl(32_92%_28%)]'}`}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">
                {d.employer_cures} employer{d.employer_cures === 1 ? '' : 's'} asked for more information
                {d.employer_cures_overdue > 0 && ` — ${d.employer_cures_overdue} past the deadline`}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Federal rules give the employee {d.employer_cure_days ?? 7} calendar days to supply it. These are already
                in the doctor's Fast-Sign screen, where the answer and the signature are one step.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-dashboard-stats">
        <Stat icon={FileText} label="Total submissions" value={d.total} />
        <Stat icon={ListChecks} label="Awaiting doctor" value={d.awaiting_doctor} />
        <Stat icon={Activity} label="Complete" value={d.complete} />
        <Stat icon={Pencil} label="Needs review" value={d.needs_review ?? 0} hint="Open patient change requests" />
      </div>
      {/* ADR 0001: billable document counts for the month, read from the billing
          ledger — abandoned patient drafts are excluded, so these match the invoice. */}
      {d.documents && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3" data-testid="admin-document-stats">
          <Stat icon={FileText} label={`Documents submitted — ${d.documents.period_label}`} value={d.documents.submitted} hint="Counted when a case reaches your queue" />
          <Stat icon={ListChecks} label="In progress" value={d.documents.in_progress} hint="Submitted, not yet complete" />
          <Stat icon={Activity} label="Completed" value={d.documents.completed} hint="Signed and delivered this month" />
        </div>
      )}
      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">Monthly completed volume</CardTitle></CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 260, minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={260}>
              <BarChart data={monthly.length === 0 ? [{ month: 'No data', count: 0 }] : monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={6} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {monthly.length === 0 && <div className="text-center text-muted-foreground text-sm py-6">No completed submissions yet — sign your first document to populate this chart.</div>}
        </CardContent>
      </Card>
    </div>
  );
}
